import {
  activateSim,
  cancelAutoSwitch,
  cancelAutoSwitchCostCentre,
  getAllAssetsForAutoSwitchCancel,
  getAllSimForActivation,
  getAutoSwitchResults,
  getAutoSwitchResultsCostCentre,
  getSimDetails,
  getSimDetailsCostCentre,
  performSimSwap,
  performSimSwapCostCentre,
  requestAutoSwitch,
  requestAutoSwitchCostCentre,
} from "./api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "../_app/lib/query";
import { DisconnectedAsset, DispatchedAsset, RequestAutoSwitchParams, SimSwapOperation } from "./types";
import { useFeedbackAlerts } from "../_app/hooks";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useSimDetails(simNumber: string, network: string, options = {}) {
  const { userType, contextId } = useContextProvider();

  const callback =
    userType === UserType.COST_CENTRE
      ? () => getSimDetailsCostCentre(contextId, simNumber, network)
      : () => getSimDetails(contextId, simNumber, network);

  return useQuery({
    queryKey: ["simDetails", simNumber, network],
    queryFn: callback,
    ...options,
  });
}

export function useRequestAutoSwitch(options = {}) {
  const { contextId, userType } = useContextProvider();

  const callback = userType === UserType.COST_CENTRE ? requestAutoSwitchCostCentre : requestAutoSwitch;

  return useMutation({
    mutationFn: (params: RequestAutoSwitchParams) => callback(contextId, params),
    ...options,
  });
}

export function useCancelAutoSwitch(options = {}) {
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId, userType } = useContextProvider();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assetId: any) =>
      userType === UserType.COST_CENTRE ? cancelAutoSwitchCostCentre(contextId, assetId) : cancelAutoSwitch(assetId),

    onSuccess: (data, assetId) => {
      queryClient.setQueriesData({ queryKey: ["disconnectSimNumbers"] }, (old: any) => {
        if (old?.list?.length) {
          old.list = old.list.map((item: DisconnectedAsset) => {
            return {
              ...item,
              requestStatus: item.id === assetId ? "PENDING" : item.requestStatus,
            };
          });
        }
        return old;
      });

      setFeedbackAlertSuccess("Your request has been submitted - this may take a few seconds for it to be applied.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useAutoSwitchResults(requestId: string, page: number, limit: number, options = {}) {
  const { contextId, userType } = useContextProvider();

  return useQuery({
    queryKey: ["autoSwitchResults", { requestId, page, limit }],

    queryFn: () => {
      if (userType === UserType.COST_CENTRE) {
        return getAutoSwitchResultsCostCentre(contextId, requestId, page, limit);
      } else {
        return getAutoSwitchResults(requestId, page, limit);
      }
    },

    ...options,
  });
}

export function useAllSimForActivation(page: number, limit: number, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["dispatchSimNumbers", { accountId, page, limit }],
    queryFn: () => getAllSimForActivation(accountId, page, limit),
    enabled: Boolean(accountId),

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useAllAssetsForAutoSwitchCancel(page: number, limit: number, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["disconnectSimNumbers", { accountId, page, limit }],
    queryFn: () => getAllAssetsForAutoSwitchCancel(accountId, page, limit),
    enabled: Boolean(accountId),

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useSimActivate(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (asset: DispatchedAsset) => activateSim(asset.id, asset.newSimNumber),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dispatchSimNumbers"],
      });
      setFeedbackAlertSuccess("Activation request submitted.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}

export function useSimSwapUpdate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { userType, contextId } = useContextProvider();

  const callback = userType === UserType.COST_CENTRE ? performSimSwapCostCentre : performSimSwap;

  return useMutation({
    mutationFn: (params: SimSwapOperation[]) => callback(contextId, params),
    ...options,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["assets"],
      });
      setFeedbackAlertSuccess("Your request has been submitted.");
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },
  });
}
